import { Box, Popper, ClickAwayListener } from "@mui/material";
import { useState, useRef, useEffect, CSSProperties } from "react";
import useTheme from "../../../../common/hooks/useTheme";

const CustomPopper = (props: any) => {
  const theme = useTheme();
  const { anchorEl, style, ...otherProps } = props;

  // Check if popper would be outside the container
  const isOutsideContainer = () => {
    if (!anchorEl) return false;

    const scrollableContainer =
      anchorEl.closest("[data-simplebar]") ||
      anchorEl.closest('[style*="overflow"]') ||
      anchorEl.closest('.MuiBox-root[style*="overflow"]');

    if (!scrollableContainer) return false;

    const containerRect = scrollableContainer.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();

    // Check if anchor is outside the visible area of the container
    const isAnchorVisible =
      anchorRect.bottom > containerRect.top &&
      anchorRect.top < containerRect.bottom &&
      anchorRect.right > containerRect.left &&
      anchorRect.left < containerRect.right;

    return !isAnchorVisible;
  };

  if (isOutsideContainer()) {
    return null; // Don't render the popper if it would be outside
  }

  return (
    <Popper
      {...otherProps}
      anchorEl={anchorEl}
      style={{
        width: anchorEl ? anchorEl.clientWidth : undefined,
        boxShadow: "var(--box-shadow-general)",
        borderRadius: "8px",
        border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
        overflowX: "hidden",
        zIndex: 1300, // Higher z-index to appear above modal content
        ...style,
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: ["top-start", "bottom-start"],
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "clippingParents",
            padding: 8,
          },
        },
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ]}
    />
  );
};

const CustomArrowIcon = ({
  focused,
  open,
}: {
  focused: boolean;
  open?: boolean;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
    }}
  >
    <path
      d="M7.99943 11.0006C7.73424 11.0005 7.47992 10.8951 7.29243 10.7076L3.29243 6.70757C3.11027 6.51897 3.00948 6.26636 3.01176 6.00417C3.01403 5.74197 3.1192 5.49116 3.30461 5.30575C3.49002 5.12034 3.74083 5.01517 4.00303 5.0129C4.26523 5.01062 4.51783 5.11141 4.70643 5.29357L7.99943 8.58657L11.2924 5.29357C11.3847 5.19806 11.495 5.12188 11.617 5.06947C11.739 5.01706 11.8703 4.98947 12.003 4.98832C12.1358 4.98717 12.2675 5.01247 12.3904 5.06275C12.5133 5.11303 12.6249 5.18728 12.7188 5.28117C12.8127 5.37507 12.887 5.48672 12.9373 5.60962C12.9875 5.73251 13.0128 5.86419 13.0117 5.99697C13.0105 6.12975 12.9829 6.26097 12.9305 6.38297C12.8781 6.50498 12.8019 6.61532 12.7064 6.70757L8.70643 10.7076C8.51894 10.8951 8.26463 11.0005 7.99943 11.0006Z"
      fill={focused ? "#1064DB" : "#3B3F42"}
    />
  </svg>
);

type SelectOption = {
  title: string;
  subtitle?: string;
  value?: string; // Optional: if different from title
};

const CustomSelect = (props: {
  label?: string;
  options: (string | SelectOption)[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  defaultValue?: string | null;
  customStyles?: CSSProperties;
  // "borderless" renders the field with no box border/background and the label
  // pinned top-left — matching the plain numeric input (CommonInputField) so it
  // blends into forms like the Add-biomarker modal instead of looking boxed.
  borderless?: boolean;
  // Muted hint shown in the value slot while nothing is selected.
  placeholder?: string;
  // The dropdown defaults to 1300 so it works inside MUI modals (also 1300).
  // Page-level selects that must stay UNDER the score side panel (drawer,
  // z-index 1200) pass something lower.
  popperZIndex?: number;
  // Rendered inside the box, just left of the arrow — an info icon with a
  // tooltip, say. Clicks on it do not open the dropdown.
  endAdornment?: React.ReactNode;
}) => {
  const [internalValue, setInternalValue] = useState<string | null>(
    props.value || props.defaultValue || null
  );
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const value = props.value !== undefined ? props.value : internalValue;
  const borderless = props.borderless;
  // Outlined only: the label sits at the top (floated) once there is a value
  // or the field is active, otherwise it rests in the vertical centre.
  const floated = Boolean(value) || isFocused || open;
  // Right padding clears the arrow, plus the adornment when there is one
  // (16px icon + 8px gap). The label is bounded by the same edge.
  const rightPadding = borderless
    ? props.endAdornment
      ? "48px"
      : "24px"
    : props.endAdornment
    ? "64px"
    : "40px";

  // Helper functions to work with mixed option types
  const getOptionValue = (option: string | SelectOption): string => {
    if (typeof option === "string") return option;
    return option.value || option.title;
  };

  const getOptionTitle = (option: string | SelectOption): string => {
    if (typeof option === "string") return option;
    return option.title;
  };

  const getSelectedOptionTitle = (): string => {
    if (!value) return "";
    const selectedOption = props.options.find(
      (option) => getOptionValue(option) === value
    );
    return selectedOption ? getOptionTitle(selectedOption) : value;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (open && anchorRef.current) {
        const scrollableContainer =
          anchorRef.current.closest("[data-simplebar]") ||
          anchorRef.current.closest('[style*="overflow"]') ||
          anchorRef.current.closest('.MuiBox-root[style*="overflow"]');

        if (scrollableContainer) {
          const containerRect = scrollableContainer.getBoundingClientRect();
          const inputRect = anchorRef.current.getBoundingClientRect();

          const isInputVisible =
            inputRect.bottom > containerRect.top &&
            inputRect.top < containerRect.bottom &&
            inputRect.right > containerRect.left &&
            inputRect.left < containerRect.right;

          if (!isInputVisible) {
            setOpen(false);
            setIsFocused(false);
          }
        }
      }
    };

    if (open) {
      const scrollableContainer =
        anchorRef.current?.closest("[data-simplebar]") ||
        anchorRef.current?.closest('[style*="overflow"]') ||
        anchorRef.current?.closest('.MuiBox-root[style*="overflow"]') ||
        document;

      scrollableContainer.addEventListener("scroll", handleScroll, true);
      return () => {
        scrollableContainer.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [open]);

  const handleClick = () => {
    setOpen(!open);
    setIsFocused(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsFocused(false);
  };

  const handleOptionClick = (option: string | SelectOption) => {
    const optionValue = getOptionValue(option);
    if (props.onChange) {
      props.onChange(optionValue);
    } else {
      setInternalValue(optionValue);
    }
    setOpen(false);
    setIsFocused(false);
  };

  return (
    <Box sx={{ flex: 1, position: "relative" }}>
      <Box
        ref={anchorRef}
        onClick={handleClick}
        sx={{
          width: "100%",
          // Outlined: 56px for a one-line label, growing with the label when
          // it wraps (the spacer below reserves its height in the flow).
          height: borderless ? "40px" : "auto",
          minHeight: borderless ? undefined : "56px",
          borderRadius: borderless ? "0px" : "8px",
          border: borderless
            ? "none"
            : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          cursor: "pointer",
          position: "relative",
          display: "flex",
          flexDirection: borderless ? "row" : "column",
          alignItems: borderless ? "flex-start" : "stretch",
          justifyContent: borderless ? undefined : "flex-end",
          boxSizing: "border-box",
          padding: borderless
            ? `18px ${rightPadding} 0px 0px`
            : `6px ${rightPadding} 8px 12px`,
          backgroundColor: borderless
            ? "transparent"
            : theme.colors_ds.NEUTRAL_BG_MAIN,
          ...props.customStyles,
          "&:hover": {
            borderColor: borderless
              ? "transparent"
              : isFocused || open
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
          },
        }}
      >
        {/* Invisible, in-flow twin of the floated label: it reserves exactly
            the label's height (one line or several), so the value row sits
            under it and the box grows when a long question wraps. The visible
            label stays absolutely positioned so it can animate between its
            resting and floated places. 4px gap keeps the value at the same
            24px it had with the old fixed padding. */}
        {!borderless && floated && (
          <Box
            aria-hidden
            sx={{
              visibility: "hidden",
              fontSize: "10px",
              fontFamily: "var(--font-Medium)",
              lineHeight: "14px",
              marginBottom: "4px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {props.label ?? "Select an option"}
          </Box>
        )}

        <Box
          // The full text on hover, since long values are truncated.
          title={getSelectedOptionTitle() || undefined}
          sx={{
            color: value
              ? theme.colors_ds.NEUTRAL_TEXT_STRONG
              : theme.colors_ds.NEUTRAL_TEXT_WEAK,
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            lineHeight: borderless ? "20px" : "24px",
            flex: borderless ? 1 : "none",
            // The field is one line tall: a long value truncates with an
            // ellipsis instead of wrapping out of it.
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {/* The placeholder only while the field is active: the label
              floats to the top in exactly those states, and with nothing
              selected the two would otherwise share the vertical centre. */}
          {getSelectedOptionTitle() ||
            (open || isFocused ? props.placeholder : "") ||
            ""}
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: borderless ? "0px" : "12px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "16px",
            width: "16px",
          }}
        >
          <CustomArrowIcon focused={isFocused || open} open={open} />
        </Box>

        {props.endAdornment && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              position: "absolute",
              right: borderless ? "24px" : "36px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "16px",
              width: "16px",
            }}
          >
            {props.endAdornment}
          </Box>
        )}
      </Box>

      {/* Custom Label */}
      <Box
        sx={{
          position: "absolute",
          // Borderless: label is always pinned top-left (matches the numeric
          // input). Outlined: it floats from the vertical center up to the top
          // once a value is present or the field is active.
          // Both states sit at the box's 12px text padding, so the resting
          // label lines up with where the value will appear.
          left: borderless ? "0px" : "12px",
          // Bounded on the right by the arrow (and adornment) so a long
          // question wraps inside the field instead of running under them.
          right: borderless ? undefined : rightPadding,
          top: borderless ? "0px" : floated ? "6px" : "50%",
          fontSize: "10px",
          fontFamily: "var(--font-Medium)",
          lineHeight: borderless ? undefined : "14px",
          whiteSpace: borderless ? undefined : "normal",
          wordBreak: borderless ? undefined : "break-word",
          color:
            isFocused || open
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : theme.colors_ds.NEUTRAL_TEXT_WEAK,
          backgroundColor: "transparent",
          transition: "all 0.2s ease-in-out",
          // Scale from the left edge: around the centre, a long resting label
          // grew leftwards past the padding ("Exercise frequency" lost ~27px).
          transformOrigin: "left center",
          transform: borderless
            ? "translateY(0) scale(1)"
            : floated
            ? "translateY(0) scale(1)"
            : "translateY(-50%) scale(1.5)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {props.label ?? "Select an option"}
      </Box>

      {/* Custom Popup using same Popper as AutoComplete */}
      <CustomPopper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={
          props.popperZIndex !== undefined
            ? { zIndex: props.popperZIndex }
            : undefined
        }
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              maxHeight: "200px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
                borderRadius: "8px",
              },
            }}
          >
            {props.options.map((option, index) => {
              const isObjectOption = typeof option === "object";
              return (
                <Box
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  sx={{
                    fontFamily: "var(--font-Medium)",
                    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                    fontSize: "14px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.colorTokens.N[40],
                    },
                  }}
                >
                  <div>{getOptionTitle(option)}</div>
                  {isObjectOption && option.subtitle && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                        marginTop: "2px",
                      }}
                    >
                      {option.subtitle}
                    </div>
                  )}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </CustomPopper>
    </Box>
  );
};

export default CustomSelect;
